import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';
import { baixarContratoCompra, podeContratoCompra } from '../utils/contratoCompra';

const fmt = v => 'R$ ' + parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const inpStyle = { width: '100%', padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };
const th = { padding: '0 12px 10px 0', color: '#9AA3AE', fontSize: '10.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
const td = { padding: '10px 12px 10px 0', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };
const card = { background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '18px 20px' };

const BASE_URL = 'https://automais.pro';
const abas = ['Dados', 'Fotos', 'Documentos', 'Despesas', 'Resumo'];

export default function VeiculoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const somenteLeitura = isVisualizacao(usuario); // visualização vê como admin, sem ações
  const isAdmin = usuario?.role === 'admin' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente' || somenteLeitura;
  const podeEditar = isAdmin && !somenteLeitura;

  const [veiculo, setVeiculo] = useState(null);
  const [aba, setAba] = useState('Dados');
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [formEdit, setFormEdit] = useState({});
  const [salvando, setSalvando] = useState(false);

  const [despesas, setDespesas] = useState([]);
  const [formDespesa, setFormDespesa] = useState({});
  const [fotos, setFotos] = useState([]);
  const fotosRef = useRef();
  const [documentos, setDocumentos] = useState([]);
  const [formDoc, setFormDoc] = useState({ nome: '', tipo: 'outro' });
  const docRef = useRef();
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const carregar = async () => {
    try {
      const r = await api.get('/veiculos/' + id);
      setVeiculo(r.data);
      setFormEdit(r.data);
      setFotos(r.data.fotos || []);
      setDespesas(r.data.despesas || []);
      setDocumentos(r.data.documentos || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, [id]);

  const salvarDados = async () => {
    setSalvando(true);
    try {
      await api.put('/veiculos/' + id, formEdit);
      setEditando(false);
      carregar();
    } catch (e) { alert(e.response?.data?.error || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const togglePublicar = async () => {
    const novo = !veiculo.publicar_site;
    try {
      await api.put('/veiculos/' + id, { ...veiculo, publicar_site: novo });
      setVeiculo(v => ({ ...v, publicar_site: novo }));
      setFormEdit(f => ({ ...f, publicar_site: novo }));
    } catch (e) { alert('Erro ao atualizar'); }
  };

  const addDespesa = async () => {
    if (!formDespesa.descricao || !formDespesa.valor || !formDespesa.data) { alert('Preencha todos os campos'); return; }
    await api.post('/veiculos/' + id + '/despesa', formDespesa);
    setFormDespesa({});
    carregar();
  };

  const delDespesa = async (did) => {
    if (!window.confirm('Remover despesa?')) return;
    await api.delete('/veiculos/' + id + '/despesa/' + did);
    carregar();
  };

  const uploadFotos = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploadingFoto(true);
    const fd = new FormData();
    for (const f of files) fd.append('fotos', f);
    try {
      await api.post('/veiculos/' + id + '/fotos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      carregar();
    } catch (err) { alert('Erro ao enviar fotos'); }
    finally { setUploadingFoto(false); if (fotosRef.current) fotosRef.current.value = ''; }
  };

  const delFoto = async (fid) => {
    if (!window.confirm('Remover foto?')) return;
    await api.delete('/veiculos/' + id + '/foto/' + fid);
    carregar();
  };

  const uploadDoc = async () => {
    const file = docRef.current?.files[0];
    if (!file) { alert('Selecione um arquivo'); return; }
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append('arquivo', file);
    fd.append('nome', formDoc.nome || file.name);
    fd.append('tipo', formDoc.tipo);
    try {
      await api.post('/veiculos/' + id + '/documento', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormDoc({ nome: '', tipo: 'outro' });
      if (docRef.current) docRef.current.value = '';
      carregar();
    } catch (err) { alert('Erro ao enviar documento'); }
    finally { setUploadingDoc(false); }
  };

  const delDoc = async (did) => {
    if (!window.confirm('Remover documento?')) return;
    await api.delete('/veiculos/' + id + '/documento/' + did);
    carregar();
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#9AA3AE' }}>Carregando...</div>;
  if (!veiculo) return <div style={{ padding: '40px', textAlign: 'center', color: '#9AA3AE' }}>Veículo não encontrado</div>;

  const totalDespesas = despesas.reduce((a, d) => a + parseFloat(d.valor || 0), 0);
  const custoTotal = parseFloat(veiculo.valor_compra || 0) + totalDespesas;
  const margem = parseFloat(veiculo.valor_venda || 0) - custoTotal;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <button onClick={() => navigate('/estoque')}
            style={{ background: 'none', border: 'none', color: '#6B7A8D', cursor: 'pointer', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", marginBottom: '6px', padding: 0 }}>
            ← Estoque
          </button>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#1C2533' }}>
            {veiculo.marca} {veiculo.modelo} <span style={{ color: '#9AA3AE', fontWeight: '400' }}>{veiculo.ano_mod}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', padding: '2px 10px', borderRadius: '20px', fontWeight: '600', background: veiculo.status === 'estoque' ? '#EAF5EE' : '#F4F6F9', color: veiculo.status === 'estoque' ? '#2D7D4F' : '#6B7A8D' }}>
              {veiculo.status}
            </span>
            <span style={{ fontSize: '12px', color: '#9AA3AE' }}>
              {Math.floor((new Date() - new Date(veiculo.criado_em)) / 86400000)} d em estoque
            </span>
            {veiculo.placa && <span style={{ fontSize: '12px', fontFamily: "'DM Mono',monospace", color: '#6B7A8D' }}>{veiculo.placa}</span>}

            {/* TOGGLE PUBLICAR NO SITE */}
            {isAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                {!somenteLeitura && <button onClick={togglePublicar}
                  style={{
                    width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                    background: veiculo.publicar_site ? '#2D7D4F' : '#D0D5DD',
                    position: 'relative', transition: 'background .2s', flexShrink: 0
                  }}>
                  <span style={{
                    position: 'absolute', top: '3px', left: veiculo.publicar_site ? '21px' : '3px',
                    width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                    transition: 'left .2s', display: 'block'
                  }}></span>
                </button>}
                <span style={{
                  fontSize: '12px', fontWeight: '600',
                  color: veiculo.publicar_site ? '#2D7D4F' : '#9AA3AE'
                }}>
                  {veiculo.publicar_site ? '🌐 No site' : 'Oculto no site'}
                </span>
                {veiculo.publicar_site && (
                  <a href={(usuario?.empresa_id === 12 ? 'https://autopmotos.com/moto.html?id=' : 'https://sales.automais.pro/moto/') + id} target='_blank' rel='noreferrer'
                    style={{ fontSize: '11px', color: '#185FA5', textDecoration: 'none', fontWeight: '600', border: '1px solid #185FA5', borderRadius: '5px', padding: '2px 7px' }}>
                    Ver no site →
                  </a>
                )}
              </div>
            )}
            {isAdmin && podeContratoCompra(veiculo) && (
              <button onClick={() => baixarContratoCompra(veiculo.id)}
                style={{ fontSize: '11px', background: 'transparent', color: '#185FA5', border: '1px solid #185FA5', borderRadius: '5px', padding: '3px 10px', cursor: 'pointer', fontWeight: '700', marginLeft: '4px' }}>
                📄 Gerar Contrato de Compra
              </button>
            )}
            {veiculo.status === 'estoque' && !somenteLeitura && (
              <button onClick={() => navigate('/vendas/nova', { state: { veiculo_id: veiculo.id } })}
                style={{ fontSize: '11px', background: '#2D7D4F', color: '#fff', border: 'none', borderRadius: '5px', padding: '4px 10px', cursor: 'pointer', fontWeight: '700', marginLeft: '4px' }}>
                💰 Registrar Venda
              </button>
            )}
          </div>
        </div>

        {/* Botões de ação */}
        {podeEditar && aba === 'Dados' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {editando ? (
              <>
                <button onClick={() => { setEditando(false); setFormEdit(veiculo); }}
                  style={{ padding: '8px 16px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontSize: '13px' }}>
                  Cancelar
                </button>
                <button onClick={salvarDados} disabled={salvando}
                  style={{ padding: '8px 16px', background: '#1A3A5C', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '700', fontSize: '13px' }}>
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            ) : (
              <button onClick={() => setEditando(true)}
                style={{ padding: '8px 16px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#1A3A5C', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', fontSize: '13px' }}>
                Editar dados
              </button>
            )}
          </div>
        )}
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '18px', borderBottom: '1px solid rgba(26,58,92,0.1)', paddingBottom: '0' }}>
        {abas.map(a => (
          <button key={a} onClick={() => { setAba(a); setEditando(false); }}
            style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: aba === a ? '2px solid #1A3A5C' : '2px solid transparent', color: aba === a ? '#1A3A5C' : '#6B7A8D', cursor: 'pointer', fontWeight: aba === a ? '700' : '400', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", marginBottom: '-1px' }}>
            {a}
          </button>
        ))}
      </div>

      {/* ABA DADOS */}
      {aba === 'Dados' && !editando && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div style={card}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '14px' }}>Identificação</div>
            {[
              ['Marca', veiculo.marca], ['Modelo', veiculo.modelo],
              ['Ano Fab / Mod', `${veiculo.ano_fab} / ${veiculo.ano_mod}`],
              ['Cor', veiculo.cor], ['Combustível', veiculo.combustivel],
              ['Chassi', veiculo.chassi], ['Renavam', veiculo.renavam],
              ['Placa', veiculo.placa || '—'], ['KM', (veiculo.km || 0).toLocaleString()],
              ...(/el[eé]tric/i.test(veiculo.combustivel || '') ? [
                ['Tipo de bateria', veiculo.num_bateria || '—'],
                ['Autonomia', veiculo.autonomia || '—'],
                ['NF-e', veiculo.numero_nfe || 'a ser emitida em até 24h'],
              ] : []),
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '0.5px solid rgba(26,58,92,0.06)' }}>
                <span style={{ fontSize: '12px', color: '#9AA3AE', fontWeight: '500' }}>{k}</span>
                <span style={{ fontSize: '13px', color: '#1C2533', fontWeight: '500', fontFamily: ['Chassi','Renavam','Placa'].includes(k) ? "'DM Mono',monospace" : 'inherit' }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={card}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '14px' }}>Entrada</div>
              {[
                ['Tipo', veiculo.tipo], ['Entrada', veiculo.tipo_entrada],
                ...(veiculo.tipo_entrada === 'compra' ? [['Comprado de', veiculo.comprado_de_nome ? veiculo.comprado_de_nome + (veiculo.comprado_de_cpf_cnpj ? ' · ' + veiculo.comprado_de_cpf_cnpj : '') : '—']] : []),
                ...(isAdmin && veiculo.tipo_entrada === 'consignado com investidor' ? [
                  ['Valor investidor', fmt(veiculo.valor_investidor)],
                  ['Valor loja', fmt(parseFloat(veiculo.valor_compra || 0) - parseFloat(veiculo.valor_investidor || 0))],
                ] : []),
                ['Status', veiculo.status], ['Cadastrado em', fmtData(veiculo.criado_em)],
                ['No site', veiculo.publicar_site ? '✅ Publicado' : '⬜ Oculto'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '0.5px solid rgba(26,58,92,0.06)' }}>
                  <span style={{ fontSize: '12px', color: '#9AA3AE', fontWeight: '500' }}>{k}</span>
                  <span style={{ fontSize: '13px', color: '#1C2533', fontWeight: '500' }}>{v}</span>
                </div>
              ))}
            </div>
            {isAdmin && (
              <div style={card}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '12px' }}>Valores</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[
                    { label: 'Compra', val: fmt(veiculo.valor_compra), c: '#1E5FA5' },
                    { label: 'Venda', val: fmt(veiculo.valor_venda), c: '#2D7D4F' },
                  ].map(item => (
                    <div key={item.label} style={{ background: '#F4F6F9', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: '#9AA3AE', marginBottom: '3px' }}>{item.label}</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: item.c }}>{item.val}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {veiculo.descricao && (
              <div style={card}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '8px' }}>Observações</div>
                <div style={{ fontSize: '13px', color: '#1C2533', lineHeight: '1.5' }}>{veiculo.descricao}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ABA DADOS — EDITANDO */}
      {aba === 'Dados' && editando && podeEditar && (
        <div style={card}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533', marginBottom: '18px' }}>Editar dados do veículo</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
            {[
              ['Marca', 'marca'], ['Modelo', 'modelo'],
              ['Ano Fab', 'ano_fab'], ['Ano Mod', 'ano_mod'],
              ['Cor', 'cor'], ['Placa', 'placa'],
              ['Chassi', 'chassi'], ['Renavam', 'renavam'],
              ['KM', 'km'], ['Valor Compra', 'valor_compra'],
              ['Valor Venda', 'valor_venda'],
            ].map(([label, campo]) => (
              <div key={campo} style={{ marginBottom: '12px' }}>
                <label style={lbl}>{label}</label>
                <input value={formEdit[campo] || ''} onChange={e => setFormEdit(f => ({ ...f, [campo]: e.target.value }))} style={inpStyle} />
              </div>
            ))}
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Combustível</label>
              <select value={formEdit.combustivel || ''} onChange={e => setFormEdit(f => ({ ...f, combustivel: e.target.value }))} style={inpStyle}>
                {['Gasolina','Flex','Etanol','Diesel','Eletrico'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            {/el[eé]tric/i.test(formEdit.combustivel || '') && <>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Tipo de Bateria</label>
                <select value={formEdit.num_bateria || ''} onChange={e => setFormEdit(f => ({ ...f, num_bateria: e.target.value }))} style={inpStyle}>
                  <option value=''>Selecione</option>
                  {['Lítio','Chumbo'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              {[['Autonomia (km)', 'autonomia'], ['NF-e', 'numero_nfe']].map(([label, campo]) => (
                <div key={campo} style={{ marginBottom: '12px' }}>
                  <label style={lbl}>{label}</label>
                  <input value={formEdit[campo] || ''} onChange={e => setFormEdit(f => ({ ...f, [campo]: e.target.value }))} style={inpStyle} />
                </div>
              ))}
            </>}
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Status</label>
              <select value={formEdit.status || ''} onChange={e => setFormEdit(f => ({ ...f, status: e.target.value }))} style={inpStyle}>
                {['estoque','vendido','consignado'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Tipo</label>
              <select value={formEdit.tipo || ''} onChange={e => setFormEdit(f => ({ ...f, tipo: e.target.value }))} style={inpStyle}>
                {['usado','novo'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={lbl}>Descrição / Observações</label>
            <input value={formEdit.descricao || ''} onChange={e => setFormEdit(f => ({ ...f, descricao: e.target.value }))} style={inpStyle} />
          </div>
          {/* Toggle publicar no site no form de edição */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: formEdit.publicar_site ? '#EAF5EE' : '#F4F6F9', borderRadius: '8px', marginBottom: '12px' }}>
            <button onClick={() => setFormEdit(f => ({ ...f, publicar_site: !f.publicar_site }))}
              style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: formEdit.publicar_site ? '#2D7D4F' : '#D0D5DD', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: '4px', left: formEdit.publicar_site ? '23px' : '4px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left .2s', display: 'block' }}></span>
            </button>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: formEdit.publicar_site ? '#2D7D4F' : '#6B7A8D' }}>
                {formEdit.publicar_site ? '🌐 Publicar no site' : 'Oculto no site'}
              </div>
              <div style={{ fontSize: '11px', color: '#9AA3AE' }}>
                {formEdit.publicar_site ? 'Esta moto aparece no catálogo público' : 'Esta moto não aparece no site'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ABA FOTOS */}
      {aba === 'Fotos' && (
        <div>
          {!somenteLeitura && <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1C2533', marginBottom: '12px' }}>fotos</div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input type='file' multiple accept='image/*' ref={fotosRef} onChange={uploadFotos}
                style={{ fontSize: '12px', color: '#6B7A8D', fontFamily: "'DM Sans',system-ui,sans-serif" }} />
              {uploadingFoto && <span style={{ color: '#1A3A5C', fontSize: '13px' }}>Enviando...</span>}
            </div>
          </div>}
          {!somenteLeitura && <div style={{ fontSize: '11px', color: '#9AA3AE', marginBottom: '8px' }}>💡 Arraste as fotos para reordenar</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '10px' }}>
            {fotos.map((f, idx) => (
              <div key={f.id}
                draggable={!somenteLeitura}
                onDragStart={e => { window._dragIdx = idx; }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const from = window._dragIdx;
                  const to = idx;
                  if (from === undefined || from === to) return;
                  setFotos(prev => {
                    const novaOrdem = [...prev];
                    const [moved] = novaOrdem.splice(from, 1);
                    novaOrdem.splice(to, 0, moved);
                    api.post('/veiculos/' + id + '/fotos/ordem', { ids: novaOrdem.map(f => f.id) }).catch(() => {});
                    return novaOrdem;
                  });
                  window._dragIdx = undefined;
                }}
                style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', background: '#F4F6F9', aspectRatio: '4/3', cursor: 'grab' }}>
                {idx === 0 && (
                  <div style={{ position: 'absolute', top: '6px', left: '6px', background: '#FF6B00', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', zIndex: 1 }}>
                    CAPA
                  </div>
                )}
                <img src={`${BASE_URL}/uploads/${f.caminho}`} alt='foto' style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', userSelect: 'none' }} />
                {!somenteLeitura && <button onClick={() => delFoto(f.id)}
                  style={{ position: 'absolute', top: '6px', right: '6px', background: '#C0392B', border: 'none', color: '#fff', width: '24px', height: '24px', borderRadius: '50%', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ×
                </button>}
              </div>
            ))}
            {fotos.length === 0 && <div style={{ color: '#9AA3AE', fontSize: '13px', padding: '20px 0' }}>Nenhuma foto cadastrada</div>}
          </div>
        </div>
      )}

      {/* ABA DOCUMENTOS */}
      {aba === 'Documentos' && (
        <div>
          {!somenteLeitura && <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1C2533', marginBottom: '12px' }}>Adicionar documento</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '10px', alignItems: 'flex-end' }}>
              <div>
                <label style={lbl}>Nome</label>
                <input value={formDoc.nome} onChange={e => setFormDoc({ ...formDoc, nome: e.target.value })} placeholder='Ex: DUT original' style={inpStyle} />
              </div>
              <div>
                <label style={lbl}>Tipo</label>
                <select value={formDoc.tipo} onChange={e => setFormDoc({ ...formDoc, tipo: e.target.value })} style={inpStyle}>
                  {['dut','crlv','laudo','contrato','outro'].map(o => <option key={o} value={o}>{o.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <input type='file' ref={docRef} style={{ fontSize: '12px', color: '#6B7A8D', fontFamily: "'DM Sans',system-ui,sans-serif" }} />
              </div>
              <button onClick={uploadDoc} disabled={uploadingDoc}
                style={{ padding: '8px 16px', background: '#1A3A5C', border: 'none', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>
                {uploadingDoc ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>}
          <div style={card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Nome','Tipo','Data',''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {documentos.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '24px 0' }}>Nenhum documento</td></tr>}
                {documentos.map(d => (
                  <tr key={d.id}>
                    <td style={{ ...td, fontWeight: '500' }}>{d.nome}</td>
                    <td style={{ ...td, color: '#6B7A8D', textTransform: 'uppercase', fontSize: '11px' }}>{d.tipo}</td>
                    <td style={{ ...td, color: '#6B7A8D', fontSize: '12px' }}>{fmtData(d.criado_em)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <a href={`${BASE_URL}/uploads/${d.caminho}`} target='_blank' rel='noreferrer'
                        style={{ color: '#1E5FA5', fontSize: '12px', marginRight: '10px', textDecoration: 'none' }}>Abrir</a>
                      {!somenteLeitura && <button onClick={() => delDoc(d.id)}
                        style={{ color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Remover</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ABA DESPESAS */}
      {aba === 'Despesas' && (
        <div>
          {!somenteLeitura && <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1C2533', marginBottom: '12px' }}>Adicionar despesa</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 140px auto', gap: '10px', alignItems: 'flex-end' }}>
              <div>
                <label style={lbl}>Descrição</label>
                <input value={formDespesa.descricao || ''} onChange={e => setFormDespesa({ ...formDespesa, descricao: e.target.value })} style={inpStyle} placeholder='Ex: Revisão, funilaria...' />
              </div>
              <div>
                <label style={lbl}>Valor</label>
                <input type='number' value={formDespesa.valor || ''} onChange={e => setFormDespesa({ ...formDespesa, valor: e.target.value })} style={inpStyle} />
              </div>
              <div>
                <label style={lbl}>Data</label>
                <input type='date' value={formDespesa.data || ''} onChange={e => setFormDespesa({ ...formDespesa, data: e.target.value })} style={inpStyle} />
              </div>
              <button onClick={addDespesa}
                style={{ padding: '8px 16px', background: '#1A3A5C', border: 'none', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>
                Adicionar
              </button>
            </div>
          </div>}
          <div style={card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Descrição','Data','Valor',''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {despesas.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '24px 0' }}>Nenhuma despesa</td></tr>}
                {despesas.map(d => (
                  <tr key={d.id}>
                    <td style={{ ...td, fontWeight: '500' }}>{d.descricao}</td>
                    <td style={{ ...td, color: '#6B7A8D', fontSize: '12px' }}>{fmtData(d.data)}</td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#C0392B', fontWeight: '600' }}>{fmt(d.valor)}</td>
                    <td style={td}>
                      {!somenteLeitura && <button onClick={() => delDespesa(d.id)}
                        style={{ color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Remover</button>}
                    </td>
                  </tr>
                ))}
                {despesas.length > 0 && (
                  <tr>
                    <td colSpan={2} style={{ ...td, fontWeight: '700', color: '#1C2533' }}>Total despesas</td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '13px', color: '#C0392B', fontWeight: '700' }}>{fmt(totalDespesas)}</td>
                    <td style={td}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ABA RESUMO */}
      {aba === 'Resumo' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div style={card}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '14px' }}>Resumo financeiro</div>
            {[
              { label: 'Valor de compra', val: fmt(veiculo.valor_compra), c: '#1E5FA5' },
              { label: 'Total despesas', val: fmt(totalDespesas), c: '#C0392B' },
              { label: 'Custo total', val: fmt(custoTotal), c: '#B06000' },
              { label: 'Valor de venda', val: fmt(veiculo.valor_venda), c: '#2D7D4F' },
              { label: 'Margem bruta', val: fmt(margem), c: margem > 0 ? '#2D7D4F' : '#C0392B' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid rgba(26,58,92,0.06)' }}>
                <span style={{ fontSize: '13px', color: '#6B7A8D' }}>{item.label}</span>
                <span style={{ fontSize: '16px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: item.c }}>{item.val}</span>
              </div>
            ))}
            <div style={{ marginTop: '14px', padding: '12px', background: margem > 0 ? '#EAF5EE' : '#FDECEA', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#9AA3AE', marginBottom: '4px' }}>Margem %</div>
              <div style={{ fontSize: '24px', fontWeight: '900', color: margem > 0 ? '#2D7D4F' : '#C0392B' }}>
                {veiculo.valor_venda > 0 ? ((margem / parseFloat(veiculo.valor_venda)) * 100).toFixed(1) + '%' : '—'}
              </div>
            </div>
          </div>
          <div style={card}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '14px' }}>Despesas detalhadas</div>
            {despesas.length === 0 ? (
              <div style={{ color: '#9AA3AE', fontSize: '13px' }}>Nenhuma despesa registrada</div>
            ) : (
              despesas.map(d => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '0.5px solid rgba(26,58,92,0.06)' }}>
                  <span style={{ fontSize: '13px', color: '#1C2533' }}>{d.descricao}</span>
                  <span style={{ fontSize: '13px', fontFamily: "'DM Mono',monospace", color: '#C0392B', fontWeight: '600' }}>{fmt(d.valor)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
